<script setup lang="ts">
  import { MpCard, MpDivider, MpText, useMpScreen } from '@borg/ui';

  const { t } = useI18n();
  const { isMobile } = useMpScreen();
  const projectConfig = useProjectConfig();
  const job = jobRepository.use();

  const employerName = computed(() => {
    if (job.value) {
      if (job.value.organization === 'incognito') {
        return t('ORGANIZATION_INCOGNITO');
      } else {
        return job.value.employer?.name ?? job.value.organization.name;
      }
    }
  });

  const salaryText = computed(() =>
    getSalaryString(
      job.value?.salary?.from,
      job.value?.salary?.to,
      job.value?.salary?.currency,
      job.value?.salary?.tax,
    ),
  );

  const showDescriptionSection = computed(
    () => !!(job.value?.description || job.value?.employmentTypes.length),
  );
  const showRequirementsSection = computed(
    () =>
      !!(
        job.value?.requirements ||
        job.value?.educationLevel.items.length ||
        job.value?.driversLicense.items.length ||
        job.value?.digitalSkills.length ||
        job.value?.languages.length ||
        job.value?.requiredWorkExperience ||
        job.value?.otherSkills.length
      ),
  );
  const showBenefitsSection = computed(() => !!job.value?.benefits);

  const locationString = computed(() =>
    job.value
      ? getWorkTypeLocationString(job.value?.workTypes ?? [], job.value?.location.summary ?? '', t)
      : undefined,
  );
</script>

<template>
  <MpCard
    v-if="job"
    :p="isMobile ? 'md' : 'lg'"
    shadow
    border
  >
    <JobTags :tags="job.tags" />
    <div class="job__title-card">
      <div class="job__main-info">
        <div class="job__title">
          <MpText
            as="h1"
            type="h2"
            no-margin
          >
            {{ job.title }}
          </MpText>
          <SaveJobButton :job="job" />
        </div>
        <MpText
          v-if="employerName"
          class="job__employer"
          as="h6"
          regular
          no-margin
        >
          {{ employerName }}
        </MpText>
      </div>
      <div class="job__additional-info">
        <SectionRowWithIcon
          v-if="job.endsAt"
          icon="calendar"
          :label="$t('JOB_DETAILS_PAGE.APPLY_TILL_LABEL')"
          :value="$d(...localeDateTimeFormat(job.endsAt, 'dmy'))"
        />
        <SectionRowWithIcon
          icon="euro"
          :label="$t('JOB_DETAILS_PAGE.SALARY_SECTION_TITLE')"
          :value="salaryText"
        />
        <SectionRowWithIcon
          icon="pin"
          :label="$t('JOB_DETAILS_PAGE.WORK_LOCATION_SECTION_TITLE')"
        >
          {{ locationString }}
        </SectionRowWithIcon>
      </div>
    </div>
    <div
      v-if="job?.isActive"
      id="job-actions"
      class="job__actions no-print"
    >
      <ChooseApplicationTypeButton source="details_top" />
      <JobShareModal />
    </div>
    <template v-if="!job.isDesigned">
      <MpDivider
        class="job__divider"
        margin="lg"
      />
      <template v-if="projectConfig.features.forceJobDetailsHtml">
        <WebView :html="job.html" />
      </template>
      <template v-else>
        <Section
          v-if="showDescriptionSection"
          :title="$t('JOB_DETAILS_PAGE.DESCRIPTION_SECTION_TITLE')"
          :content="job.description"
        >
          <SectionRow
            :label="$t('JOB_DETAILS_PAGE.JOB_TYPE_SECTION_TITLE')"
            :value="job.employmentTypes.join(', ')"
          />
        </Section>
        <Section
          v-if="showRequirementsSection"
          :title="$t('JOB_DETAILS_PAGE.EDUCATION_REQUIREMENTS_SECTION_TITLE')"
          :content="job.requirements"
        >
          <SectionRow
            :label="$t('JOB_DETAILS_PAGE.REQUIRED_EDUCATION_SECTION_TITLE')"
            :value="job.educationLevel.items.join(', ')"
          />
          <SectionRow
            :label="$t('JOB_DETAILS_PAGE.DRIVER_LICENSE_SECTION_TITLE')"
            :value="job.driversLicense.items.join(', ')"
          />
          <SectionRow
            :label="$t('JOB_DETAILS_PAGE.DIGITAL_SKILLS_SECTION_TITLE')"
            :value="job.digitalSkills.join(', ')"
          />
          <SectionRow
            :label="$t('JOB_DETAILS_PAGE.LANGUAGE_SECTION_TITLE')"
            :value="job.languages.map((lang) => `${lang.id} (${lang.level})`).join(', ')"
          />
          <SectionRow
            :label="$t('JOB_DETAILS_PAGE.MINIMAL_EXPERIENCE_SECTION_TITLE')"
            :value="job.requiredWorkExperience ?? undefined"
          />
          <SectionRow
            :label="$t('JOB_DETAILS_PAGE.ADDITIONAL_SKILLS_SECTION_TITLE')"
            :value="job.otherSkills.join(', ')"
          />
        </Section>
        <Section
          v-if="showBenefitsSection"
          :title="$t('JOB_DETAILS_PAGE.EMPLOYMENT_BENEFITS_SECTION_TITLE')"
          :content="job.benefits"
        />
      </template>
    </template>
  </MpCard>
</template>

<style scoped lang="scss">
  .job {
    &__title-card {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-60);
    }

    &__main-info {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-50);
    }

    &__additional-info {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-50);
    }

    &__title {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: var(--mp-space-60);
    }

    &__employer {
      font-size: 20px;
      color: var(--mp-color-text-20);
    }

    &__actions {
      display: flex;
      gap: var(--mp-space-30);
      margin-top: var(--mp-space-60);
    }

    &__divider {
      --padding: var(--mp-space-60);
      width: calc(100% + 2 * var(--padding));
      position: relative;
      left: calc(-1 * var(--padding));

      @include screen-md {
        --padding: var(--mp-space-70);
      }
    }
  }
</style>
